/* Live workflow shape and diff operation types shared by the spec diff modules. */

export type LiveNode = {
  id: string;
  nodeType: string;
  configJson: Record<string, unknown> | null;
  orderIndex: number;
};

export type LiveEdge = {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: string;
};

export type LiveWorkflow = {
  id: string;
  name: string;
  description: string | null;
  preflight: string | null;
  cleanup: string | null;
  status: string;
  /**
   * Pause toggle. Suite inclusion is derived eligibility (approved AND passed at
   * least once); `paused` excludes an otherwise-eligible flow from suite runs.
   */
  paused?: boolean;
  flowType: string;
  apiOnly?: boolean;
  /**
   * Live tag rows attached to this workflow. Diff treats `tags` as a name-set
   * (no ids on the spec side), so the comparison projects to sorted names.
   */
  tags?: Array<{ name: string }>;
  /**
   * Live environment constraints. Diff projects to a name-set, mirroring how
   * the spec author writes them (the API expects ids on write, which sync.ts
   * resolves from the property's environments).
   */
  environmentConstraints?: LiveEnvironmentConstraints | null;
  /** Sandbox template name (already resolved id->name on the GET response). */
  sandboxTemplate?: { id: string; name: string } | null;
  /**
   * Teardown linkage on seed workflows. The DB row has these directly; the GET
   * response carries the resolved teardown workflow name when the FK is set,
   * so pull can emit by-name without an extra lookup.
   */
  teardownWorkflowId?: string | null;
  teardownWorkflowName?: string | null;
  teardownSnapshotVersion?: number | null;
  teardownEnabled?: boolean | null;
  /**
   * Declared seed outputs from `seedOutputsJson`. The GET response should
   * resolve `propertyId` -> `propertyName` for round-trip cleanliness; when it
   * doesn't, the diff falls back to id-form comparison (which won't match
   * spec-side names -- surfaces as "changed" -- but won't silently clobber the
   * data either).
   */
  seedOutputs?: Array<{
    dataKey: string;
    name?: string;
    displayLabel?: string;
    description?: string;
    propertyId?: string;
    propertyName?: string;
    /** Opaque preserved data -- credentialOutput, attributes -- sidecar PR will surface. */
    credentialOutput?: unknown;
    attributes?: unknown;
  }>;
  inputsJson: unknown;
  scenariosJson: unknown;
  nodes: LiveNode[];
  /** Edges are optional for back-compat with diff callers; sync needs them for reconcile. */
  edges?: LiveEdge[];
};

export type LiveEnvironmentConstraints =
  | { mode: 'only'; environmentIds: string[]; environmentNames?: string[] }
  | { mode: 'envTypes'; types: Array<'permanent' | 'ephemeral' | 'local' | 'sandbox'> };

export type SpecOp =
  | {
      kind: 'create';
      /** Position of the corresponding spec node in EmittedWorkflow.nodes. */
      specIndex: number;
      nodeType: string;
      configJson: Record<string, unknown>;
      /** Source line of the spec lead-in, so writeback knows where to inject `@id`. */
      sourceLine?: number;
    }
  | {
      kind: 'update';
      nodeId: string;
      /** Same `specIndex` semantics as `create`. */
      specIndex: number;
      nodeType: string;
      /** Full replacement configJson (the route merges, so this overrides). */
      configJson: Record<string, unknown>;
      /** What changed -- for the human summary printout. */
      changes: {
        configChanged: boolean;
      };
    }
  | { kind: 'delete'; nodeId: string; nodeType: string };

export type WorkflowFieldChanges = {
  description?: string | null;
  preflight?: string | null;
  cleanup?: string | null;
  inputsJson?: unknown;
  scenariosJson?: unknown;
  name?: string;
  status?: 'draft' | 'approved';
  flowType?: 'standard' | 'setup' | 'seed' | 'teardown';
  apiOnly?: boolean;
  /** Tag names -- sync.ts resolves to ids and calls the bulk-tags endpoint. */
  tags?: string[];
  /**
   * Environment constraints (name-form). sync.ts resolves environmentNames ->
   * environmentIds before PUT. `null` clears constraints.
   */
  environmentConstraints?:
    | { mode: 'only'; environmentNames: string[] }
    | { mode: 'envTypes'; types: Array<'permanent' | 'ephemeral' | 'local' | 'sandbox'> }
    | null;
  /** Sandbox template name; sync.ts resolves to id. `null` clears the template. */
  sandboxTemplate?: string | null;
  /**
   * Teardown linkage (seed workflows only). Spec carries the teardown spec
   * NAME; sync.ts resolves to the teardown workflow id and PATCHes the seed.
   * `null` on `teardownSpec` clears the link. `enabled`/`snapshotVersion`
   * surface independently so a snapshot pin or disable doesn't require
   * renaming the link.
   */
  teardownSpec?: string | null;
  teardownSnapshotVersion?: 'latest' | number | null;
  teardownEnabled?: boolean;
  /**
   * Spec-form declared outputs. Names -> ids resolved by sync.ts. The diff
   * preserves `credentialOutput`/`attributes` from the live row 1:1 -- the spec
   * doesn't author those yet, so we never want to clear them by accident.
   */
  seedOutputs?: Array<{
    dataKey: string;
    label?: string;
    description?: string;
    propertyName?: string;
    /** When a live entry carries this, sync.ts preserves it on the wire. */
    credentialOutput?: unknown;
    attributes?: unknown;
  }>;
};

export type DiffResult = {
  ops: SpecOp[];
  workflowFieldChanges: WorkflowFieldChanges;
  /** True when current default edges do not match the spec's linear node order. */
  edgeChanges: boolean;
  /** Spec names a different name than the live workflow (we'll update to match). */
  nameDrift: { from: string; to: string } | null;
  /** Spec has two nodes claiming the same `@id` -- second one is treated as new. */
  duplicateSpecIds: string[];
  /** Spec node references an `@id` that doesn't exist on the live workflow. */
  orphanedSpecIds: string[];
};

export class NodeTypeMutationError extends Error {
  constructor(
    public readonly nodeId: string,
    fromType: string,
    toType: string
  ) {
    super(
      `Node ${nodeId} cannot change type from "${fromType}" to "${toType}". ` +
        `Remove the @id from the spec and let the diff CREATE a fresh node.`
    );
    this.name = 'NodeTypeMutationError';
  }
}
